import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { take } from 'rxjs/operators';
import * as moment from 'moment';
import App from '../../helpers/app';
import config from '../../config';
import { formatarDataHoraBR } from '../../helpers/date.helper';
import { WhatsAppService, WhatsAppInstancia, InstanciaStatusResponse } from '../../shared/services/whatsapp.service';

@Component({
  selector: 'app-whatsapp-instancias',
  templateUrl: './whatsapp-instancias.component.html',
  styleUrls: ['./whatsapp-instancias.component.scss']
})
export class WhatsappInstanciasComponent implements OnInit, OnDestroy {
  instancias: WhatsAppInstancia[] = [];
  novaInstancia = { name: '' };
  editarInstancia: { id: number | null; name: string } = { id: null, name: '' };
  instanciaConectando: WhatsAppInstancia | null = null;
  statusConectando: InstanciaStatusResponse | null = null;
  pollingInterval: any = null;
  enviarMsg = { number: '', text: '' };
  instanciaEnviar: WhatsAppInstancia | null = null;
  criandoInstancia = false;
  enviandoMensagem = false;

  constructor(
    private whatsapp: WhatsAppService,
    private modal: NgbModal,
    private spinner: NgxSpinnerService,
    private app: App
  ) {}

  ngOnInit() {
    this.carregarInstancias();
  }

  ngOnDestroy() {
    this.pararPolling();
  }

  private isErrorResponse(res: any): boolean {
    return res != null && typeof res === 'object' && 'status' in res && Number((res as { status?: number }).status) >= 400;
  }

  async carregarInstancias() {
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
    try {
      const res = await this.whatsapp.listarInstancias().pipe(take(1)).toPromise() as WhatsAppInstancia[] | { return?: WhatsAppInstancia[]; data?: WhatsAppInstancia[]; status?: number } | undefined;
      if (this.isErrorResponse(res)) {
        this.instancias = [];
        await this.app.alert('Erro', 'Não foi possível carregar as instâncias. Tente novamente.', 'error');
        return;
      }
      this.instancias = Array.isArray(res) ? res : (res?.return ?? res?.data ?? []);
    } catch (e) {
      this.instancias = [];
      await this.app.alert('Erro', 'Não foi possível carregar as instâncias. Tente novamente.', 'error');
    } finally {
      this.spinner.hide();
    }
  }

  openModalNova(content: any) {
    this.novaInstancia = { name: '' };
    this.criandoInstancia = false;
    this.modal.open(content, { centered: true });
  }

  async criarInstancia(modal: any) {
    const name = (this.novaInstancia.name || '').trim();
    if (!name) {
      await this.app.alert('Atenção', 'Informe o nome da instância.', 'warning');
      return;
    }
    this.criandoInstancia = true;
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
    try {
      const res = await this.whatsapp.criarInstancia({ name }).pipe(take(1)).toPromise();
      if (this.isErrorResponse(res)) {
        await this.app.alert('Erro', 'Não foi possível criar a instância. Tente novamente.', 'error');
        return;
      }
      await this.app.alert('Sucesso', 'Instância criada com sucesso.', 'success');
      modal.close('Close click');
      await this.carregarInstancias();
    } catch (e) {
      await this.app.alert('Erro', 'Não foi possível criar a instância. Tente novamente.', 'error');
    } finally {
      this.criandoInstancia = false;
      this.spinner.hide();
    }
  }

  openModalEditar(content: any, inst: WhatsAppInstancia) {
    this.editarInstancia = { id: inst.id, name: inst.nome };
    this.modal.open(content, { centered: true });
  }

  async salvarNome(modal: any) {
    const name = (this.editarInstancia.name || '').trim();
    if (this.editarInstancia.id == null || !name) {
      await this.app.alert('Atenção', 'Informe o nome.', 'warning');
      return;
    }
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
    try {
      const res = await this.whatsapp.editarInstancia(this.editarInstancia.id, { name }).pipe(take(1)).toPromise();
      if (this.isErrorResponse(res)) {
        await this.app.alert('Erro', 'Não foi possível atualizar o nome. Tente novamente.', 'error');
        return;
      }
      await this.app.alert('Sucesso', 'Nome atualizado.', 'success');
      modal.close('Close click');
      await this.carregarInstancias();
    } catch (e) {
      await this.app.alert('Erro', 'Não foi possível atualizar o nome. Tente novamente.', 'error');
    } finally {
      this.spinner.hide();
    }
  }

  openModalConectar(content: any, inst: WhatsAppInstancia) {
    this.instanciaConectando = inst;
    this.statusConectando = null;
    this.modal.open(content, { centered: true, size: 'sm' });
    this.iniciarConectar();
  }

  async iniciarConectar() {
    if (!this.instanciaConectando) return;
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
    try {
      const res = await this.whatsapp.conectarInstancia(this.instanciaConectando.id).pipe(take(1)).toPromise();
      if (this.isErrorResponse(res)) {
        await this.app.alert('Erro', 'Não foi possível iniciar a conexão. Tente novamente.', 'error');
        return;
      }
      await this.atualizarStatusConectando();
      this.iniciarPolling();
    } catch (e) {
      await this.app.alert('Erro', 'Não foi possível iniciar a conexão. Tente novamente.', 'error');
    } finally {
      this.spinner.hide();
    }
  }

  async atualizarStatusConectando() {
    if (!this.instanciaConectando) return;
    try {
      const res = await this.whatsapp.statusInstancia(this.instanciaConectando.id).pipe(take(1)).toPromise();
      if (this.isErrorResponse(res)) return;
      this.statusConectando = res as InstanciaStatusResponse;
      const data = res as InstanciaStatusResponse;
      if (data?.instance) {
        this.instanciaConectando = data.instance;
        if (data.instance.status === 'connected' || data.instance.status === 'disconnected') {
          this.pararPolling();
          await this.carregarInstancias();
          if (data.instance.status === 'connected') {
            await this.app.alert('Sucesso', 'Instância conectada ao WhatsApp.', 'success');
          }
        }
      }
    } catch (_) {}
  }

  iniciarPolling() {
    this.pararPolling();
    this.pollingInterval = setInterval(() => this.atualizarStatusConectando(), 5000);
  }

  pararPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  fecharModalConectar(modal: any) {
    this.pararPolling();
    this.instanciaConectando = null;
    this.statusConectando = null;
    modal.close('Close click');
    this.carregarInstancias();
  }

  openModalEnviar(content: any, inst: WhatsAppInstancia) {
    this.instanciaEnviar = inst;
    this.enviarMsg = { number: '', text: '' };
    this.enviandoMensagem = false;
    this.modal.open(content, { centered: true });
  }

  async enviarMensagem(modal: any) {
    const number = (this.enviarMsg.number || '').trim();
    const text = (this.enviarMsg.text || '').trim();
    if (!this.instanciaEnviar || !number || !text) {
      await this.app.alert('Atenção', 'Preencha número e texto.', 'warning');
      return;
    }
    const allowWithoutConnected = !!(config as any).whatsapp?.allowSendWithoutConnectedInstance;
    const instanciaDesconectada = this.instanciaEnviar.status !== 'connected';
    if (!allowWithoutConnected && instanciaDesconectada) {
      await this.app.alert('Atenção', 'Nenhuma instância WhatsApp conectada. Conecte a instância no painel ou habilite o envio para fila na configuração.', 'warning');
      return;
    }
    this.enviandoMensagem = true;
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
    try {
      const resEnvio = await this.whatsapp.enviarTexto(this.instanciaEnviar.id, { number, text }).pipe(take(1)).toPromise();
      if (resEnvio != null && typeof resEnvio === 'object' && 'status' in resEnvio && Number((resEnvio as { status?: number }).status) >= 400) {
        await this.app.alert('Erro', 'O envio não foi realizado. Tente novamente.', 'error');
        return;
      }
      const foiParaFila = (allowWithoutConnected && instanciaDesconectada) || (resEnvio != null && typeof resEnvio === 'object' && (resEnvio as { queued?: boolean }).queued === true);
      if (foiParaFila) {
        await this.app.alert('Sucesso', 'A mensagem foi colocada na fila e será enviada em até 1 dia para o número do servidor.', 'success');
      } else {
        await this.app.alert('Sucesso', 'Mensagem enviada.', 'success');
      }
      modal.close('Close click');
      this.enviarMsg = { number: '', text: '' };
      this.instanciaEnviar = null;
    } catch (e) {
      await this.app.alert('Erro', 'O envio não foi realizado. Tente novamente.', 'error');
    } finally {
      this.enviandoMensagem = false;
      this.spinner.hide();
    }
  }

  async excluir(inst: WhatsAppInstancia) {
    const ok = await this.app.confirm('Confirmar', `Excluir a instância "${inst.nome}"? Esta ação não pode ser desfeita.`);
    if (!ok) return;
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
    try {
      const res = await this.whatsapp.deletarInstancia(inst.id).pipe(take(1)).toPromise();
      if (this.isErrorResponse(res)) {
        await this.app.alert('Erro', 'Não foi possível excluir a instância. Tente novamente.', 'error');
        return;
      }
      await this.app.alert('Sucesso', 'Instância excluída.', 'success');
      await this.carregarInstancias();
    } catch (e) {
      await this.app.alert('Erro', 'Não foi possível excluir a instância. Tente novamente.', 'error');
    } finally {
      this.spinner.hide();
    }
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      connected: 'Conectado',
      connecting: 'Conectando',
      disconnected: 'Desconectado'
    };
    return map[status] || status;
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      connected: 'badge-success',
      connecting: 'badge-warning',
      disconnected: 'badge-secondary'
    };
    return map[status] || 'badge-secondary';
  }

  formatarData(s: string | null): string {
    return formatarDataHoraBR(s);
  }

  /** Data/hora de última desconexão no padrão Brasil (UTC-3): normaliza para -6h em relação ao valor do servidor. */
  formatarDataDesconexao(s: string | null): string {
    if (!s) return '–';
    try {
      const str = typeof s === 'string' && /T\d{2}:\d{2}/.test(s) ? s.replace(/Z$/i, '') + 'Z' : s;
      const m = moment.utc(str).subtract(6, 'hours');
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm:ss') : String(s);
    } catch {
      return String(s);
    }
  }
}
